import { useState, useEffect, useCallback } from 'react';
import { getDailyQuestion, answerDailyQuestion, skipDailyQuestion } from '../services/chaptersAPI';

/**
 * Hook for Daily Question of the Day.
 *
 * - Fetches GET /api/daily-question/today/ on mount
 * - Sets showModal = true only if has_question && user_status is null
 * - Exposes: questionData, showModal, handleAnswer, handleSkip, dismissModal, loading
 */
export function useDailyQuestion() {
  const [questionData, setQuestionData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchQuestion() {
      try {
        const data = await getDailyQuestion();
        if (cancelled) return;
        setQuestionData(data);
        // Show modal only if there's a question and user hasn't interacted yet
        if (data.has_question && !data.user_status) {
          setShowModal(true);
        }
      } catch (err) {
        // Silently fail — don't block the page
        console.error('Failed to fetch daily question:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchQuestion();
    return () => { cancelled = true; };
  }, []);

  const handleAnswer = useCallback(async (answerData, mediaFiles = []) => {
    if (!questionData?.question?.id) return;
    const result = await answerDailyQuestion(questionData.question.id, answerData, mediaFiles);
    setShowModal(false);
    setQuestionData((prev) => prev ? {
      ...prev,
      user_status: 'answered',
      answer: result.answer || {
        title: answerData.title || prev?.question?.question_text,
        text: answerData.text,
        life_event_id: result.life_event_id,
        memory_id: result.memory_id,
      },
    } : prev);
    return result;
  }, [questionData]);

  const handleSkip = useCallback(async () => {
    if (!questionData?.question?.id) return;
    await skipDailyQuestion(questionData.question.id);
    setShowModal(false);
    setQuestionData((prev) => prev ? { ...prev, user_status: 'skipped' } : prev);
  }, [questionData]);

  const dismissModal = useCallback(() => {
    setShowModal(false);
  }, []);

  return {
    questionData,
    showModal,
    handleAnswer,
    handleSkip,
    dismissModal,
    loading,
  };
}
